import { useEffect, useState } from "react";
import { api, euro, formatDate, API } from "@/lib/api";
import { Receipt, Plus, Download, Mail, FileText, Trash2, FileSpreadsheet, Send } from "lucide-react";
import { toast } from "sonner";

const CAUSALI = {
  quota: "Quota associativa annuale",
  contributo: "Contributo liberale straordinario",
  corso: "Quota corso/attività",
};
const METHODS = {
  contanti: "Contanti",
  pos: "POS / Carta / Bancomat",
  bonifico: "Bonifico Bancario",
  paypal: "PayPal",
};

export default function Receipts({ user }) {
  const canManage = user?.is_admin || user?.role === "tesoriere";
  const [items, setItems] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(emptyForm(year));

  function emptyForm(y) {
    return {
      recipient_name: "",
      birth_place: "",
      birth_date: "",
      codice_fiscale: "",
      residence_city: "",
      residence_province: "",
      residence_via: "",
      in_qualita_di: "socio",
      tessera_number: "",
      amount: "",
      causale: "quota",
      corso_description: "",
      payment_method: "contanti",
      fiscal_year: y,
      recipient_email: "",
      note: "",
      send_email: true,
      donation_id: null,
    };
  }

  const load = () => {
    api.get(`/receipts?fiscal_year=${year}`).then(r => setItems(r.data)).catch(() => {});
    api.get(`/receipts/pending-quota`).then(r => setPendingQuota(r.data)).catch(() => {});
  };
  const [pendingQuota, setPendingQuota] = useState(null);
  const [showPending, setShowPending] = useState(true);
  const MEMBERSHIP_FEE = 20;

  const prefillFrom = (u) => {
    setForm({
      ...emptyForm(year),
      recipient_name: u.name || "",
      codice_fiscale: u.codice_fiscale || "",
      residence_via: u.address || "",
      recipient_email: u.email || "",
      amount: String(MEMBERSHIP_FEE),
      causale: "quota",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prefillFromDonation = (d) => {
    const method = ["contanti", "bonifico", "pos", "paypal"].includes(d.method) ? d.method : "bonifico";
    setForm({
      ...emptyForm(year),
      recipient_name: d.donor_name || "",
      codice_fiscale: d.donor_codice_fiscale || "",
      recipient_email: d.donor_email || "",
      amount: String(d.amount ?? ""),
      causale: "contributo",
      payment_method: method,
      donation_id: d.id,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.recipient_name || !form.amount) { toast.error("Nome e importo obbligatori"); return; }
    const amount = parseFloat(form.amount);
    if (!(amount > 0)) { toast.error("Importo non valido"); return; }
    if (form.codice_fiscale && !/^[A-Z0-9]{16}$/.test(form.codice_fiscale.toUpperCase())) {
      toast.error("Codice Fiscale non valido (16 caratteri alfanumerici)"); return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        amount,
        fiscal_year: parseInt(form.fiscal_year) || new Date().getFullYear(),
        codice_fiscale: form.codice_fiscale ? form.codice_fiscale.toUpperCase() : null,
        birth_date: form.birth_date || null,
        birth_place: form.birth_place || null,
        residence_city: form.residence_city || null,
        residence_province: form.residence_province || null,
        residence_via: form.residence_via || null,
        tessera_number: form.tessera_number || null,
        corso_description: form.corso_description || null,
        recipient_email: form.recipient_email || null,
        note: form.note || null,
        donation_id: form.donation_id || null,
      };
      const r = await api.post("/receipts", payload);
      toast.success(`Ricevuta N. ${r.data.receipt_number} emessa`);
      window.dispatchEvent(new CustomEvent("giacomo:say", { detail: { text: "Ricevuta pronta, avanti il prossimo! 🧾" } }));
      setShowForm(false);
      setForm(emptyForm(year));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore emissione");
    } finally { setSending(false); }
  };

  const [resendingAll, setResendingAll] = useState(false);
  const resendAll = async () => {
    if (!window.confirm(`Reinviare via email tutte le ricevute valide del ${year}?`)) return;
    setResendingAll(true);
    try {
      const r = await api.post(`/receipts/resend-all?fiscal_year=${year}`);
      toast.success(`${r.data.sent} ricevute reinviate${r.data.skipped_no_email ? ` · ${r.data.skipped_no_email} senza email` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile reinviare");
    } finally { setResendingAll(false); }
  };

  const resend = async (id) => {
    try {
      await api.post(`/receipts/${id}/send`);
      toast.success("Email inviata al destinatario");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile inviare");
    }
  };

  const deleteReceipt = async (r) => {
    if (!window.confirm(`Eliminare la ricevuta N. ${r.receipt_number}?\n\nIl numero verrà liberato e riusato dalla prossima ricevuta. Potrai poi rifarla.`)) return;
    try {
      await api.delete(`/receipts/${r.id}`);
      toast.success(`Ricevuta ${r.receipt_number} eliminata · numero liberato`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const openFiscalReport = () => {
    window.open(`${API}/admin/fiscal-report/pdf?year=${year}`, "_blank");
  };

  const yearsOpts = Array.from({length: 5}).map((_, i) => new Date().getFullYear() - i);

  if (!canManage) {
    return (
      <div data-testid="section-receipts" className="paper rounded-2xl p-8 text-center text-[#6B6558]">
        <Receipt className="w-8 h-8 mx-auto mb-3 text-[#8A8272]" />
        <div className="font-display text-lg text-[#2B3A33] mb-1">Sezione riservata al Tesoriere</div>
        <div className="text-sm">Solo il Tesoriere e l'Owner possono emettere e consultare le ricevute di pagamento.</div>
      </div>
    );
  }

  return (
    <div data-testid="section-receipts">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-2"><Receipt className="w-3 h-3" /> Ricevute</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">Ricevute di Pagamento</h1>
          <p className="text-[#6B6558] mt-2">Emetti la ricevuta ufficiale per quote, contributi e corsi. Il PDF è pronto per stampa e invio automatico via email.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select data-testid="rc-year-select" value={year} onChange={e => setYear(parseInt(e.target.value))} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8] bg-white">
          {yearsOpts.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button
          data-testid="new-receipt-btn"
          onClick={() => setShowForm(v => !v)}
          className="terracotta-btn px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold"
        >
          <Plus className="w-4 h-4" /> {showForm ? "Annulla" : "Nuova Ricevuta"}
        </button>
        <button
          data-testid="resend-all-receipts-btn"
          disabled={resendingAll}
          onClick={resendAll}
          className="px-4 py-2.5 rounded-xl border border-[#E8E2D8] bg-white font-semibold inline-flex items-center gap-2 hover:bg-[#FDF2F2] disabled:opacity-60"
          title="Reinvia via email tutte le ricevute valide dell'anno selezionato"
        >
          <Send className="w-4 h-4" /> {resendingAll ? "Invio…" : "Reinvia tutte via email"}
        </button>
        <button
          data-testid="fiscal-report-btn"
          onClick={openFiscalReport}
          className="px-4 py-2.5 rounded-xl border border-[#2B3A33] bg-[#2B3A33] text-white font-semibold inline-flex items-center gap-2 hover:opacity-90"
          title="Rendiconto entrate/uscite pronto per il commercialista"
        >
          <FileSpreadsheet className="w-4 h-4" /> Report Fiscale {year}
        </button>
      </div>

      {pendingQuota && (pendingQuota.pending.length > 0 || pendingQuota.legacy_auto.length > 0 || (pendingQuota.donations || []).length > 0) && (
        <div className="paper rounded-2xl p-5 mb-6" data-testid="pending-quota-panel">
          <button onClick={() => setShowPending(v => !v)} className="w-full flex items-center justify-between text-left">
            <div className="font-display text-lg font-bold text-[#2B3A33]">
              Ricevute da trasmettere {year} <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold">{pendingQuota.pending.length}</span>
            </div>
            <span className="text-xs text-[#8A8272]">{showPending ? "Nascondi" : "Mostra"}</span>
          </button>
          {showPending && (
            <div className="mt-3 divide-y divide-[#E8E2D8]">
              {pendingQuota.pending.map(u => (
                <div key={u.user_id} className="py-2.5 flex flex-wrap items-center gap-2 justify-between" data-testid={`pending-quota-${u.user_id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#2B3A33] truncate">{u.name} {u.status === "pending" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] ml-1">in attesa</span>}</div>
                    <div className="text-xs text-[#6B6558] truncate">{u.email}{u.codice_fiscale ? ` · ${u.codice_fiscale}` : " · CF mancante"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.paypal_payment_at && <span className="text-xs px-3 py-1.5 rounded-lg bg-[#EFF6FF] text-[#1D4ED8] font-semibold border border-[#3B82F6]/30" title={`Rif. ${u.paypal_payment_tx}`} data-testid={`paypal-${u.user_id}`}>PayPal € {Number(u.paypal_payment_amount).toFixed(2)} · {new Date(u.paypal_payment_at).toLocaleDateString("it-IT")}</span>}
                    {u.payment_proof_file_id
                      ? <a href={`${API}/admin/members/${u.user_id}/document?which=proof`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-[#F0FDF4] text-[#166534] font-semibold border border-[#16A34A]/30" data-testid={`proof-${u.user_id}`}>💶 Vedi pagamento</a>
                      : <span className="text-xs px-3 py-1.5 rounded-lg bg-[#F5F1EA] text-[#8A8272]">Nessuna prova caricata</span>}
                    <button onClick={() => prefillFrom(u)} className="text-xs px-3 py-1.5 rounded-lg bg-[#EF3E36] text-white font-semibold" data-testid={`issue-${u.user_id}`}>Emetti ricevuta</button>
                  </div>
                </div>
              ))}
              {(pendingQuota.donations || []).length > 0 && (
                <div className="pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#8A8272] font-semibold mb-1">Donazioni confermate — ricevuta da emettere</div>
                  {pendingQuota.donations.map(d => (
                    <div key={d.id} className="py-2.5 flex flex-wrap items-center gap-2 justify-between" data-testid={`pending-donation-${d.id}`}>
                      <div className="min-w-0">
                        <div className="font-medium text-[#2B3A33] truncate">{d.donor_name} <span className="text-xs text-[#8A8272]">· € {Number(d.amount).toFixed(2)} · {d.method}</span></div>
                        <div className="text-xs text-[#6B6558] truncate">{d.donor_email || "email mancante"}{d.donor_codice_fiscale ? ` · ${d.donor_codice_fiscale}` : ""}</div>
                      </div>
                      <button onClick={() => prefillFromDonation(d)} className="text-xs px-3 py-1.5 rounded-lg bg-[#EF3E36] text-white font-semibold" data-testid={`issue-donation-${d.id}`}>Emetti ricevuta</button>
                    </div>
                  ))}
                </div>
              )}
              {pendingQuota.legacy_auto.length > 0 && (
                <div className="pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#8A8272] font-semibold mb-1">Ricevute quota emesse automaticamente (storico)</div>
                  {pendingQuota.legacy_auto.map(u => (
                    <div key={u.user_id} className="py-1.5 flex items-center justify-between gap-2 text-sm">
                      <span className="truncate">{u.name} · <span className="font-mono text-xs">{u.quota_receipt_number}</span></span>
                      <a href={u.download_url} target="_blank" rel="noreferrer" className="text-xs text-[#EF3E36] font-semibold hover:underline shrink-0" data-testid={`legacy-${u.user_id}`}>Scarica PDF</a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="paper p-6 rounded-2xl mb-6 space-y-4" data-testid="receipt-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Cognome e Nome *</label>
              <input required data-testid="rc-name" value={form.recipient_name} onChange={e => setForm(f => ({...f, recipient_name: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8]" placeholder="Bravato Francesco" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Codice Fiscale</label>
              <input maxLength={16} data-testid="rc-cf" value={form.codice_fiscale} onChange={e => setForm(f => ({...f, codice_fiscale: e.target.value.toUpperCase()}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8] font-mono uppercase" placeholder="RSSMRA80A01H501U" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Nato/a a</label>
              <input data-testid="rc-birthplace" value={form.birth_place} onChange={e => setForm(f => ({...f, birth_place: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8]" placeholder="Avola" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Data di nascita</label>
              <input type="date" data-testid="rc-birthdate" value={form.birth_date} onChange={e => setForm(f => ({...f, birth_date: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8]" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Città residenza</label>
              <input data-testid="rc-city" value={form.residence_city} onChange={e => setForm(f => ({...f, residence_city: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8]" placeholder="Siracusa" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Provincia</label>
              <input maxLength={2} data-testid="rc-province" value={form.residence_province} onChange={e => setForm(f => ({...f, residence_province: e.target.value.toUpperCase()}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8] uppercase" placeholder="SR" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Via</label>
              <input data-testid="rc-via" value={form.residence_via} onChange={e => setForm(f => ({...f, residence_via: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8]" placeholder="Ludovico Ariosto 3" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">In qualità di</label>
              <select data-testid="rc-quality" value={form.in_qualita_di} onChange={e => setForm(f => ({...f, in_qualita_di: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8] bg-white">
                <option value="socio">Socio</option>
                <option value="terzo">Terzo / Sostenitore</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">N° Tessera (se socio)</label>
              <input data-testid="rc-tessera" value={form.tessera_number} onChange={e => setForm(f => ({...f, tessera_number: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8] font-mono" placeholder="AMN-0003" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Anno fiscale</label>
              <input type="number" data-testid="rc-year" value={form.fiscal_year} onChange={e => setForm(f => ({...f, fiscal_year: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8]" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Importo €</label>
              <input required type="number" step="0.01" data-testid="rc-amount" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8]" placeholder="20.00" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Causale</label>
              <select data-testid="rc-causale" value={form.causale} onChange={e => setForm(f => ({...f, causale: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8] bg-white">
                <option value="quota">Quota associativa</option>
                <option value="contributo">Contributo liberale</option>
                <option value="corso">Quota corso/attività</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Modalità</label>
              <select data-testid="rc-method" value={form.payment_method} onChange={e => setForm(f => ({...f, payment_method: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8] bg-white">
                <option value="contanti">Contanti</option>
                <option value="pos">POS / Carta</option>
                <option value="bonifico">Bonifico</option>
                <option value="paypal">PayPal</option>
              </select>
            </div>
          </div>

          {form.causale === "corso" && (
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Descrizione corso/attività</label>
              <input data-testid="rc-corso-desc" value={form.corso_description} onChange={e => setForm(f => ({...f, corso_description: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8]" placeholder="Corso di fotografia — Marzo 2026" />
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Email destinatario (opzionale — riceverà il PDF)</label>
            <input type="email" data-testid="rc-email" value={form.recipient_email} onChange={e => setForm(f => ({...f, recipient_email: e.target.value}))} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8E2D8]" placeholder="socio@example.com" />
          </div>

          <label className="flex items-center gap-2 text-sm text-[#3C4A44]">
            <input type="checkbox" checked={form.send_email} onChange={e => setForm(f => ({...f, send_email: e.target.checked}))} data-testid="rc-send-email" />
            Manda subito la ricevuta via email al destinatario
          </label>

          <div className="flex gap-2">
            <button type="submit" disabled={sending} data-testid="rc-submit" className="terracotta-btn px-5 py-2.5 rounded-xl font-semibold disabled:opacity-60">
              {sending ? "Emissione..." : "Emetti ricevuta"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8]">Annulla</button>
          </div>
        </form>
      )}

      <div className="paper rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-0 sm:min-w-[640px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-[#8A8272] border-b border-[#E8E2D8]">
              <th className="p-4">N°</th>
              <th className="p-4">Data</th>
              <th className="p-4">Destinatario</th>
              <th className="p-4 hidden md:table-cell">Causale</th>
              <th className="p-4 hidden lg:table-cell">Modalità</th>
              <th className="p-4 text-right">Importo</th>
              <th className="p-4 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="7" className="p-8 text-center text-[#6B6558]">
                <FileText className="w-6 h-6 mx-auto mb-2 text-[#8A8272]" />
                Nessuna ricevuta emessa per {year}
              </td></tr>
            )}
            {items.map(r => (
              <tr key={r.id} data-testid={`receipt-${r.id}`} className={`border-b border-[#E8E2D8]/60 ${r.voided ? "opacity-60" : ""}`}>
                <td className="p-4 font-mono font-bold text-[#2B3A33]">
                  {r.receipt_number}
                  {r.voided && <div className="text-[10px] uppercase tracking-widest text-[#EF3E36] font-bold mt-1">Annullata</div>}
                </td>
                <td className="p-4 text-[#6B6558]">{formatDate(r.created_at)}</td>
                <td className="p-4">
                  <div className={`font-medium text-[#2B3A33] ${r.voided ? "line-through" : ""}`}>{r.recipient_name}</div>
                  {r.codice_fiscale && <div className="text-[10px] text-[#8A8272] font-mono">{r.codice_fiscale}</div>}
                  {r.voided && r.void_reason && (
                    <div className="text-[10px] text-[#EF3E36] italic mt-1">Motivo: {r.void_reason}</div>
                  )}
                </td>
                <td className="p-4 hidden md:table-cell text-[#6B6558]">{CAUSALI[r.causale] || r.causale}</td>
                <td className="p-4 hidden lg:table-cell text-[#6B6558]">{METHODS[r.payment_method] || r.payment_method}</td>
                <td className={`p-4 text-right font-mono font-bold ${r.voided ? "text-[#8A8272] line-through" : "text-[#16A34A]"}`}>{euro(r.amount)}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <a href={`${API}/receipts/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`rc-pdf-${r.id}`} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-[#2B3A33] text-white font-semibold hover:opacity-90 mr-1">
                    <Download className="w-3 h-3" /> PDF
                  </a>
                  {!r.voided && r.recipient_email && (
                    <button onClick={() => resend(r.id)} data-testid={`rc-resend-${r.id}`} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E8E2D8] hover:bg-[#FDF2F2] mr-1">
                      <Mail className="w-3 h-3" /> Rinvia
                    </button>
                  )}
                  {!r.voided && (
                    <button onClick={() => deleteReceipt(r)} data-testid={`rc-delete-${r.id}`} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-[#FCA5A5] bg-[#FEF2F2] text-[#991B1B] font-semibold hover:bg-[#FEE2E2]">
                      <Trash2 className="w-3 h-3" /> Elimina
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
